import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';

const EASE = [0.22, 1, 0.36, 1];

/**
 * PageHero - Reusable premium hero for inner pages.
 * - pageKey: (optional) key to look up a custom hero image in admin settings
 * - eyebrow: small uppercase label
 * - title: big headline (string, supports `highlight` marked portion)
 * - highlight: portion of title rendered with gradient (required for the split)
 * - subtitle: optional subtitle text
 * - image: fallback background image URL
 * - alt: fallback alt text (kept for SEO)
 * - breadcrumbs: array of { name, href? } for crumb display
 */
export const PageHero = ({
  pageKey,
  eyebrow,
  title,
  highlight,
  subtitle,
  image,
  alt = '',
  breadcrumbs = [],
  children,
  minHeight = 'min-h-[65vh]',
}) => {
  // Look up an override from admin content (falls back to `image`/`alt`)
  const hero = useHeroImage(pageKey || '___none___', image, alt);

  // Split title around highlight word (if found)
  const parts = highlight && title.includes(highlight) ? title.split(highlight) : null;

  return (
    <section
      className={`relative bg-dark overflow-hidden ${minHeight} flex items-center`}
      data-testid="page-hero"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img decoding="async"
          src={hero.image_url}
          alt={hero.alt}
          className="w-full h-full object-cover opacity-30 ken-burns"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-dark via-dark/92 to-primary-dark/40" />
      <div className="absolute inset-0 mesh-gradient opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-20 w-[40vw] h-[40vw] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-custom relative z-10 py-20 md:py-28 lg:py-32 w-full">
        <div className="max-w-4xl">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <motion.nav
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="flex items-center gap-2 text-xs text-white/50 mb-6 tracking-wider"
              aria-label="Breadcrumb"
            >
              {breadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-2">
                  {crumb.href ? (
                    <Link
                      to={crumb.href}
                      className="hover:text-primary-glow transition-colors"
                    >
                      {crumb.name}
                    </Link>
                  ) : (
                    <span className="text-white/80">{crumb.name}</span>
                  )}
                  {i < breadcrumbs.length - 1 && (
                    <ChevronRight className="h-3 w-3 opacity-50" />
                  )}
                </span>
              ))}
            </motion.nav>
          )}

          {/* Eyebrow */}
          {eyebrow && (
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
              className="inline-block text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase mb-4"
            >
              ━━ {eyebrow}
            </motion.span>
          )}

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            className="font-heading text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tight mb-6"
          >
            {parts ? (
              <>
                {parts[0]}
                <span className="text-gradient-primary italic font-display inline-block pr-2">
                  {highlight}
                </span>
                {parts[1]}
              </>
            ) : (
              title
            )}
          </motion.h1>

          {/* Subtitle */}
          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
              className="text-white/75 text-base md:text-lg max-w-2xl leading-relaxed"
            >
              {subtitle}
            </motion.p>
          )}

          {/* Optional children (CTAs etc.) */}
          {children && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
              className="mt-8"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
